import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, FileText, CheckCircle2, AlertTriangle, Receipt, ExternalLink } from 'lucide-react';
import api from '../lib/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PayslipsPage() {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [assigning, setAssigning] = useState({});
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/employees/list/simple').then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const handleFile = (f) => { setFile(f); setReport(null); };

  const distribute = async () => {
    if (!file) return;
    setLoading(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/payslips/distribute', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setReport(res.data);
    } catch (e) { alert(e.response?.data?.detail || 'Errore elaborazione'); }
    finally { setLoading(false); }
  };

  const assignPage = async (page, employeeId, periodo) => {
    if (!file || !employeeId) return;
    setAssigning(a => ({ ...a, [page]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('page', page);
      fd.append('employee_id', employeeId);
      fd.append('periodo', periodo || '');
      const res = await api.post('/payslips/assign-page', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const emp = employees.find(e => e.id === employeeId);
      setReport(r => ({
        ...r,
        unmatched: r.unmatched.filter(u => u.page !== page),
        matched: [...r.matched, { page, employee_name: emp ? `${emp.last_name} ${emp.first_name}` : '', periodo, match_mode: 'manuale', codice_fiscale: '', file_url: res.data?.file_url }],
        matched_count: r.matched_count + 1,
        unmatched_count: r.unmatched_count - 1,
      }));
    } catch (e) { alert(e.response?.data?.detail || 'Errore assegnazione'); }
    finally { setAssigning(a => ({ ...a, [page]: false })); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payslips-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Receipt size={24} /> Buste Paga</h1>
        <p className="text-sm text-slate-500">Carica un PDF con tutte le buste paga: vengono smistate automaticamente nei documenti di ogni dipendente leggendo il codice fiscale.</p>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
            className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#1a4b8c] hover:bg-slate-50 transition-colors"
            data-testid="payslip-dropzone"
          >
            <Upload size={32} className="mx-auto text-slate-400 mb-2" />
            {file ? (
              <p className="text-sm text-slate-700 font-medium flex items-center justify-center gap-2"><FileText size={16} /> {file.name}</p>
            ) : (
              <p className="text-sm text-slate-500">Trascina qui il PDF delle buste paga o clicca per selezionarlo</p>
            )}
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="payslip-input" />
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={distribute} disabled={!file || loading} data-testid="distribute-btn">
              {loading ? 'Elaborazione in corso...' : 'Smista buste paga'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {report && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-green-200 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-green-700"><CheckCircle2 size={16} /> Smistate ({report.matched_count})</CardTitle></CardHeader>
            <CardContent className="p-0 max-h-96 overflow-y-auto">
              {report.matched.length === 0 ? <p className="text-sm text-slate-400 p-4">Nessuna busta smistata</p> : (
                <div className="divide-y">
                  {report.matched.map((m, i) => (
                    <div key={i} className="px-4 py-2 flex items-center justify-between gap-2" data-testid={`matched-${m.page}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{m.employee_name}</p>
                        <p className="text-[11px] text-slate-500">Pag. {m.page} · {m.periodo} · <span className="italic">{m.match_mode}</span></p>
                      </div>
                      {m.file_url && <a href={`${BACKEND}${m.file_url}`} target="_blank" rel="noreferrer" className="text-blue-600 flex-shrink-0"><ExternalLink size={14} /></a>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-amber-200 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-amber-700"><AlertTriangle size={16} /> Non riconosciute ({report.unmatched_count})</CardTitle></CardHeader>
            <CardContent className="p-0 max-h-96 overflow-y-auto">
              {report.unmatched.length === 0 ? <p className="text-sm text-slate-400 p-4">Tutte le buste sono state smistate 🎉</p> : (
                <div className="divide-y">
                  {report.unmatched.map((u) => (
                    <div key={u.page} className="px-4 py-2 space-y-1" data-testid={`unmatched-${u.page}`}>
                      <p className="text-[11px] text-slate-500">Pag. {u.page} · {u.periodo} · CF: {u.codice_fiscale || 'non letto'}</p>
                      <Select onValueChange={(v) => assignPage(u.page, v, u.periodo)} disabled={assigning[u.page]}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`assign-select-${u.page}`}><SelectValue placeholder={assigning[u.page] ? 'Assegnazione...' : 'Assegna a dipendente'} /></SelectTrigger>
                        <SelectContent>
                          {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
